import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Stack
} from "@mui/material";

/* ---------- Style Maps ---------- */

const SEV_COLOR = {
  Critical: { bg: "#FCEBEB", color: "#791F1F" },
  High: { bg: "#FAEEDA", color: "#633806" },
  Low: { bg: "#EAF3DE", color: "#27500A" },
};

const DEC_COLOR = {
  "Dead stock": { bg: "#FCEBEB", color: "#791F1F" },
  "Likely dead soon": { bg: "#FAEEDA", color: "#633806" },
  "Reorder now": { bg: "#E6F1FB", color: "#0C447C" },
  "Healthy fast moving": { bg: "#EAF3DE", color: "#27500A" },
};

/* ---------- Small Components ---------- */

const DecisionBadge = ({ label }) => {
  if (!label) return null;
  const style = DEC_COLOR[label] || {};
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        background: style.bg,
        color: style.color,
        fontSize: 10,
        height: 22,
        borderRadius: "4px",
      }}
    />
  );
};

const SeverityBadge = ({ label }) => {
  if (!label) return null;
  const style = SEV_COLOR[label] || {};
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        background: style.bg,
        color: style.color,
        fontSize: 10,
        height: 22,
        borderRadius: "999px",
        fontWeight: 500,
      }}
    />
  );
};

const ProductCell = ({ name, sku, category }) => (
  <Box>
    <Typography fontSize={13} fontWeight={500}>
      {name}
    </Typography>
    <Typography fontSize={11} color="text.secondary">
      {sku} · {category}
    </Typography>
  </Box>
);

/* ---------- Main Component ---------- */

const PrioritySection = ({ data = [], loading = false }) => {
  const urgentCount = data.filter(
    (item) =>
      item.severity_label === "Critical" ||
      item.severity_label === "High"
  ).length;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "error.light",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "#fff5f5",
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "error.light",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "error.main",
            }}
          />
          <Typography fontSize={12} fontWeight={500} color="error.main">
            {urgentCount} items need your attention today
          </Typography>
        </Stack>
      </Box>

      {/* Table */}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#fafafa" }}>
            <TableCell sx={thStyle}>Product</TableCell>
            <TableCell sx={thStyle}>Status</TableCell>
            <TableCell sx={thStyle}>Action required</TableCell>
            <TableCell sx={thStyle}>Severity</TableCell>
            <TableCell sx={thStyle}>Score</TableCell>
            <TableCell sx={thStyle}>Cash at risk</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))
            : data.map((item) => (
                <PriorityRow key={item.sku_code} item={item} />
              ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

/* ---------- Row ---------- */

const PriorityRow = ({ item }) => {
  const isCritical = item.severity_label === "Critical";

  return (
    <TableRow
      sx={{
        background: isCritical ? "rgba(226,75,74,0.04)" : "transparent",
        "&:hover": { background: "#fafafa" },
      }}
    >
      <TableCell>
        <ProductCell
          name={item.product_name}
          sku={item.sku_code}
          category={item.category}
        />
      </TableCell>

      <TableCell>
        <DecisionBadge label={item.decision_label} />
      </TableCell>

      <TableCell sx={tdText}>
        {item.action_text}
      </TableCell>

      <TableCell>
        <SeverityBadge label={item.severity_label} />
      </TableCell>

      <TableCell sx={tdStrong}>
        {item.priority_score}
      </TableCell>

      <TableCell sx={{ ...tdStrong, color: "error.main" }}>
        ₹{item.inventory_cost_value.toLocaleString()}
      </TableCell>
    </TableRow>
  );
};

/* ---------- Skeleton ---------- */

const SkeletonRow = () => (
  <TableRow>
    {Array.from({ length: 6 }).map((_, i) => (
      <TableCell key={i}>
        <Box
          sx={{
            height: 10,
            width: "80%",
            background: "#eee",
            borderRadius: 1,
          }}
        />
      </TableCell>
    ))}
  </TableRow>
);

/* ---------- Reusable Styles ---------- */

const thStyle = {
  fontSize: 10.5,
  textTransform: "uppercase",
  color: "text.secondary",
  fontWeight: 500,
};

const tdText = {
  fontSize: 12,
  color: "text.secondary",
  maxWidth: 260,
};

const tdStrong = {
  fontSize: 12.5,
  fontWeight: 500,
};

/* ---------- Export ---------- */

export default PrioritySection;